// Modules
import { useCallback, useContext } from 'react'
import { nanoid } from 'nanoid'

// Hooks
import useLanguage from 'hooks/system/useLanguage'

// Context
import Data from 'context/Data'
import Dispatch from 'context/Dispatch'

// Reducers
import { NOTICE_ACTIONS } from 'reducers/notice'

// Constants
import { IS_LIVE } from 'constants/environment'

// Hook: System > Notices
function useNotices () {
	// Data
	const { notices } = useContext(Data)
	const dispatch = useContext(Dispatch)
	const { checkIfTranslatable, translate } = useLanguage('components.utility.notice')

	// Functions (Memoized)
	const addNotice = useCallback(originalNotice => {
		const notice = { ...originalNotice }
		// Add notice type if it's missing
		if (!notice.type) notice.type = 'information'
		// Log errors if possible (TEMPORARY)
		if (!IS_LIVE && notice.type === 'error' && !!notice.log) console.error(`AIX | ${notice.code}`, notice.log)
		// Set a message if it's missing
		if (!notice.message) {
			// Ignore notices without type
			if (!notice.code || !checkIfTranslatable('global', `notices.${notice.type}.${notice.code}`, notice.data || {})) {
				// Set fallback message for error or success notices
				if (notice.type === 'error' || notice.type === 'success') notice.message = translate.global(`notices.${notice.type}.fallback`)
				else return
			} else {
				// Set translated message
				notice.message = translate.global(`notices.${notice.type}.${notice.code}`, notice.data || {})
				// Add error code as hint if it's missing
				if (notice.type === 'error' && !notice.hint) notice.hint = notice.code
			}
		}
		const id = notice.id || nanoid()
		dispatch({
			type: NOTICE_ACTIONS.ADD_ITEMS,
			payload: { id, willPersist: false, ...notice }
		})
		return id
	}, [checkIfTranslatable, dispatch, translate])
	const removeNotice = useCallback(id => {
		dispatch({ type: NOTICE_ACTIONS.REMOVE_ITEMS, id })
		return id
	}, [dispatch])

	// Response
	return { addNotice, notices, removeNotice }
}

// Export
export default useNotices
